import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccountBalance as AccountBalanceIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Pending as PendingIcon,
  Payment as PaymentIcon,
  AccountBalanceWallet as WalletIcon,
  LinkOff as LinkOffIcon,
  Sync as SyncIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StripeConnectExpressAccount } from '../../models/Organization';
import SEO from '../../components/SEO';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import type { SnackbarSeverity } from '../../components/common/NotificationSnackbar';
import {
  getStripeConnectClient,
  isStripeConnectAllowed,
  resolveOrganizationType,
  StripeConnectVariant,
} from '../../services/stripeConnectClient';
import StripePayoutHistory from '../../components/stripe/StripePayoutHistory';

type AccountStatus =
  | 'not_connected'
  | 'action_required'
  | 'onboarding_required'
  | 'pending_verification'
  | 'fully_connected';

interface StripeConnectPageProps {
  variant?: StripeConnectVariant;
}

const StripeConnectPage: React.FC<StripeConnectPageProps> = ({ variant = 'host' }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const stripeClient = useMemo(() => getStripeConnectClient(variant), [variant]);
  const [stripeAccount, setStripeAccount] = useState<StripeConnectExpressAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');

  const fetchStripeAccount = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProfileError(null);

    try {
      const organizationType = await resolveOrganizationType(variant);
      const canUseStripe = isStripeConnectAllowed(variant, organizationType);
      setAllowed(canUseStripe);

      if (!canUseStripe) {
        if (!organizationType) {
          setProfileError(
            t('stripeConnect.missingOrganizationType', 'Organization type is unavailable. Please contact support.'),
          );
        }
        setStripeAccount(null);
        return;
      }

      try {
        const account = await stripeClient.getStripeConnectExpressAccount();
        setStripeAccount(account);
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError(t('stripeConnect.fetchError', 'Failed to fetch Stripe account status'));
        }
      }
    } catch {
      setProfileError(t('stripeConnect.profileFetchError', 'Failed to load profile data required for Stripe Connect.'));
      setAllowed(false);
      setStripeAccount(null);
    } finally {
      setLoading(false);
    }
  }, [stripeClient, t, variant]);

  useEffect(() => {
    fetchStripeAccount();
  }, [fetchStripeAccount]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const account = await stripeClient.createStripeConnectExpressAccount();
      setStripeAccount(account);
      if (account.onboardingUrl) {
        window.open(account.onboardingUrl, '_blank');
      }
    } catch (err) {
      setError(t('stripeConnect.connectError', 'Failed to create Stripe Connect account'));
    } finally {
      setConnecting(false);
    }
  };

  const openExternal = (url?: string | null) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenDashboard = () => openExternal(stripeAccount?.accountDashboardUrl);

  const handleUpdateAccount = () => {
    openExternal(stripeAccount?.onboardingUrl || stripeAccount?.accountDashboardUrl);
  };

  const handleResync = async () => {
    setResyncing(true);
    setError(null);
    try {
      const account = await stripeClient.resyncStripeConnectExpressAccount();
      setStripeAccount(account);
      setSnackbarMessage(t('stripeConnect.resyncSuccess', 'Stripe account status updated'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(t('stripeConnect.resyncError', 'Failed to sync account status from Stripe'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setResyncing(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await stripeClient.disconnectStripeConnectExpressAccount();
      setStripeAccount(null);
      setSnackbarMessage(t('stripeConnect.disconnectSuccess', 'Stripe account disconnected successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDisconnectDialogOpen(false);
    } catch (err) {
      setSnackbarMessage(t('stripeConnect.disconnectError', 'Failed to disconnect Stripe account'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDisconnecting(false);
    }
  };

  const getAccountStatus = (): AccountStatus => {
    if (!stripeAccount) return 'not_connected';
    if (stripeAccount.actionRequired) return 'action_required';
    if (!stripeAccount.detailsSubmitted) return 'onboarding_required';
    if (!stripeAccount.chargesEnabled || !stripeAccount.payoutsEnabled) return 'pending_verification';
    return 'fully_connected';
  };

  const status = getAccountStatus();

  const needsAccountUpdate = Boolean(
    stripeAccount && (stripeAccount.actionRequired || stripeAccount.onboardingUrl),
  );
  const showUpdateCta =
    needsAccountUpdate &&
    (status === 'action_required' ||
      status === 'onboarding_required' ||
      status === 'pending_verification');

  const statusMeta = useMemo(() => {
    switch (status) {
      case 'fully_connected':
        return {
          color: theme.palette.success.main,
          icon: <CheckCircleIcon sx={{ fontSize: 28 }} />,
          title: t('stripeConnect.fullyConnected', 'Fully Connected'),
          description: t(
            'stripeConnect.fullyConnectedDescription',
            'Your Stripe Connect account is fully set up and ready to accept payments!',
          ),
        };
      case 'action_required':
        return {
          color: theme.palette.warning.main,
          icon: <WarningIcon sx={{ fontSize: 28 }} />,
          title: t('stripeConnect.actionRequired', 'Action Required'),
          description: t(
            'stripeConnect.actionRequiredDescription',
            'Your Stripe Connect account requires immediate attention. Please click the "Update Account" button below to complete the required actions in your Stripe account.',
          ),
        };
      case 'onboarding_required':
        return {
          color: theme.palette.info.main,
          icon: <PendingIcon sx={{ fontSize: 28 }} />,
          title: t('stripeConnect.onboardingRequired', 'Onboarding Required'),
          description: t(
            'stripeConnect.onboardingDescription',
            'Your Stripe Connect account has been created. Please complete the onboarding process to start accepting payments.',
          ),
        };
      case 'pending_verification':
        return {
          color: theme.palette.warning.main,
          icon: <PendingIcon sx={{ fontSize: 28 }} />,
          title: t('stripeConnect.pendingVerification', 'Pending Verification'),
          description: t(
            'stripeConnect.pendingDescription',
            'Your account is being reviewed by Stripe. This usually takes 1-2 business days. You can continue onboarding while waiting.',
          ),
        };
      default:
        return {
          color: theme.palette.warning.main,
          icon: <WarningIcon sx={{ fontSize: 28 }} />,
          title: t('stripeConnect.notConnected', 'Not Connected to Stripe'),
          description:
            variant === 'tutor'
              ? t(
                  'stripeConnect.tutorConnectDescription',
                  'Connect your Stripe account to receive online payments for tutor lessons.',
                )
              : t(
                  'stripeConnect.connectDescription',
                  'Connect your Stripe account to start accepting payments for your bookings. This will allow you to receive payments directly to your bank account.',
                ),
        };
    }
  }, [status, t, theme.palette, variant]);

  const showCapabilityTiles = status === 'pending_verification' || status === 'fully_connected';

  const wrongFlowMessage =
    variant === 'host'
      ? t(
          'stripeConnect.useTutorFlow',
          'Stripe Connect for individual tutor organizations is managed from the tutor menu.',
        )
      : t(
          'stripeConnect.useHostFlow',
          'Stripe Connect for business organizations is managed from host organization settings.',
        );

  const pageSubtitle =
    variant === 'tutor'
      ? t(
          'stripeConnect.tutorSubtitle',
          'Receive lesson payments directly to your bank account via Stripe Connect.',
        )
      : t(
          'stripeConnect.subtitle',
          'Accept online payments for bookings and get paid out to your organization bank account.',
        );

  const renderCapabilityTile = (
    label: string,
    enabled: boolean,
    icon: React.ReactNode,
  ) => (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: 2.5,
        background: `linear-gradient(135deg, ${alpha(
          enabled ? theme.palette.success.main : theme.palette.warning.main,
          0.16,
        )}, ${alpha(theme.palette.common.white, 0.02)})`,
        borderColor: alpha(enabled ? theme.palette.success.main : theme.palette.warning.main, 0.28),
      }}
    >
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
              {label}
            </Typography>
            <Box sx={{ mt: 0.75 }}>
              <Chip
                size="small"
                label={
                  enabled
                    ? t('stripeConnect.enabled', 'Enabled')
                    : t('stripeConnect.pending', 'Pending')
                }
                color={enabled ? 'success' : 'warning'}
                sx={{ fontWeight: 700 }}
              />
            </Box>
          </Box>
          <Box sx={{ color: enabled ? 'success.light' : 'warning.light', opacity: 0.9 }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <SEO title={t('stripeConnect.title', 'Stripe Connect')} noindex={true} />
      <Box
        sx={{
          // DashboardLayout `main` is a column flex scroller; without flexShrink:0 the page
          // box is compressed and paddingBottom no longer clears the fixed site footer.
          flexShrink: 0,
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 2, sm: 3, md: 4 },
          // Same rhythm as subscription page; main already clears the fixed footer.
          pb: 2,
          maxWidth: 880,
          width: '100%',
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            pb: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ maxWidth: 640 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                  border: 1,
                  borderColor: alpha(theme.palette.primary.main, 0.28),
                }}
              >
                <AccountBalanceIcon sx={{ color: 'primary.light', fontSize: 22 }} />
              </Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {t('stripeConnect.title', 'Stripe Connect')}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, lineHeight: 1.6 }}>
              {pageSubtitle}
            </Typography>
          </Box>
        </Box>

        {profileError && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {profileError}
          </Alert>
        )}

        {allowed === false && !profileError && (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            {wrongFlowMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {allowed && (
          <Stack spacing={2.5}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: alpha(theme.palette.common.white, 0.03),
                borderColor: alpha(statusMeta.color, 0.28),
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 18px 48px rgba(0,0,0,0.35)'
                    : '0 10px 28px rgba(0,0,0,0.08)',
                backgroundImage: `radial-gradient(ellipse 70% 80% at 0% 50%, ${alpha(
                  statusMeta.color,
                  0.18,
                )}, transparent 55%), radial-gradient(ellipse 50% 70% at 100% 50%, ${alpha(
                  theme.palette.primary.main,
                  0.08,
                )}, transparent 50%)`,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 5,
                  bgcolor: statusMeta.color,
                  display: { xs: 'none', sm: 'block' },
                }}
              />
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                {stripeAccount && (
                  <Stack
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    sx={{
                      position: 'absolute',
                      top: { xs: 10, sm: 14 },
                      right: { xs: 10, sm: 14 },
                      zIndex: 2,
                    }}
                  >
                    <Tooltip title={t('stripeConnect.resync', 'Sync status')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleResync}
                          disabled={resyncing || disconnecting}
                          aria-label={t('stripeConnect.resync', 'Sync status')}
                          sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                        >
                          {resyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    {needsAccountUpdate && !showUpdateCta && (
                      <Tooltip title={t('stripeConnect.updateAccount', 'Update Account')}>
                        <IconButton
                          size="small"
                          onClick={handleUpdateAccount}
                          aria-label={t('stripeConnect.updateAccount', 'Update Account')}
                          sx={{ color: 'warning.main', '&:hover': { color: 'warning.light' } }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title={t('stripeConnect.disconnect', 'Disconnect Account')}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDisconnectDialogOpen(true)}
                          disabled={disconnecting}
                          aria-label={t('stripeConnect.disconnect', 'Disconnect Account')}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main' },
                          }}
                        >
                          {disconnecting ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <LinkOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ pl: { sm: 1 }, pr: stripeAccount ? { xs: 9, sm: 10 } : 0 }}
                >
                  <Stack direction="row" spacing={1.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        color: statusMeta.color,
                        bgcolor: alpha(statusMeta.color, 0.14),
                        border: 1,
                        borderColor: alpha(statusMeta.color, 0.28),
                      }}
                    >
                      {statusMeta.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.25, mb: 0.75 }}
                      >
                        {statusMeta.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 560 }}>
                        {statusMeta.description}
                      </Typography>
                    </Box>
                  </Stack>

                  {(status === 'not_connected' || showUpdateCta) && (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.25}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {status === 'not_connected' && (
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={connecting ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />}
                          onClick={handleConnect}
                          disabled={connecting}
                          sx={{ fontWeight: 700, px: 2.5, py: 1.1, whiteSpace: 'nowrap' }}
                        >
                          {connecting
                            ? t('stripeConnect.connecting', 'Connecting...')
                            : t('stripeConnect.connect', 'Connect Stripe Account')}
                        </Button>
                      )}

                      {showUpdateCta && (
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<LinkIcon />}
                          onClick={handleUpdateAccount}
                          sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                        >
                          {t('stripeConnect.updateAccount', 'Update Account')}
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>

                {stripeAccount && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 2.5, ml: { sm: 1 }, gap: 1.5 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {t('stripeConnect.accountId', 'Stripe Account ID')}
                      </Typography>
                      <Chip
                        label={stripeAccount.id}
                        variant="outlined"
                        size="small"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      />
                    </Stack>

                    {stripeAccount.accountDashboardUrl && status !== 'action_required' && (
                      <Button
                        variant="contained"
                        startIcon={<OpenInNewIcon />}
                        onClick={handleOpenDashboard}
                        sx={{ fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        {t('stripeConnect.accountDashboard', 'Account Dashboard')}
                      </Button>
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {showCapabilityTiles && stripeAccount && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {renderCapabilityTile(
                  t('stripeConnect.chargesStatus', 'Charges'),
                  stripeAccount.chargesEnabled,
                  <PaymentIcon sx={{ fontSize: 28 }} />,
                )}
                {renderCapabilityTile(
                  t('stripeConnect.payoutsStatus', 'Payouts'),
                  stripeAccount.payoutsEnabled,
                  <WalletIcon sx={{ fontSize: 28 }} />,
                )}
              </Stack>
            )}

            {variant === 'host' && stripeAccount && <StripePayoutHistory />}
          </Stack>
        )}
      </Box>

      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
        aria-labelledby="disconnect-dialog-title"
        aria-describedby="disconnect-dialog-description"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle id="disconnect-dialog-title" sx={{ fontWeight: 800 }}>
          {t('stripeConnect.disconnectConfirmTitle', 'Disconnect Stripe Account')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="disconnect-dialog-description">
            {t(
              'stripeConnect.disconnectConfirmMessage',
              'Are you sure you want to disconnect your Stripe account? This will prevent you from receiving payments for new bookings. You can reconnect at any time.',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDisconnectDialogOpen(false)} disabled={disconnecting}>
            {t('stripeConnect.disconnectCancelButton', 'Cancel')}
          </Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            variant="contained"
            disabled={disconnecting}
            startIcon={disconnecting ? <CircularProgress size={16} /> : <LinkOffIcon />}
          >
            {disconnecting
              ? t('stripeConnect.disconnecting', 'Disconnecting...')
              : t('stripeConnect.disconnectConfirmButton', 'Disconnect')}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default StripeConnectPage;
